import React from 'react';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';

import { isToday } from 'date-fns/isToday';

import { formatDate } from '@sg/garnish';

import { type WantedTime } from '../../../machine';
import { adjustWantedTime } from '../../../utils';
import { BagHeaderLink } from '../BagHeaderLink';

/**
 * Time selector for the bag header.
 */
export const BagTimeHeader = (props: BagTimeHeaderProps) => {
  const { time, isDisabled, requestTimeChange } = props;

  const dateInfo = useDateInfo(time);
  const { formatMessage } = useIntl();

  return (
    <BagHeaderLink
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      isDisabled={isDisabled}
      onPress={requestTimeChange}
    >
      <FormattedMessage
        {...messages.time}
        values={{
          state: dateInfo.state,
          day: dateInfo.day,
          time: dateInfo.time,
        }}
      />
    </BagHeaderLink>
  );
};

export const BagTimeConnector = (props: Pick<BagTimeHeaderProps, 'time'>) => {
  const { time } = props;

  const dateInfo = useDateInfo(time);

  return <FormattedMessage {...messages.timePrefix} values={{ state: dateInfo.state }} />;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useDateInfo = (time?: WantedTime) => {
  const validTime = adjustWantedTime(time);
  const { formatMessage } = useIntl();

  if (!validTime) {
    return {
      time: formatMessage({
        defaultMessage: 'soon',
        description: 'Bag | Header | Soon',
      }),
      day: '',
      state: 'invalid' as const,
    };
  }

  if (isToday(validTime)) {
    return {
      time: formatDate(validTime, 'h:mma'),
      day: '',
      state: 'today' as const,
    };
  }

  return {
    time: formatDate(validTime, 'h:mma'),
    day: formatDate(validTime, 'EEE'),
    state: 'future' as const,
  };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly timePrefix: { readonly state: string };
  readonly time: {
    readonly day: MessageValue;
    readonly state: string;
    readonly time: MessageValue;
  };
  readonly accessibilityLabel: NoMessageValues;
}>({
  timePrefix: {
    defaultMessage: `{state, select,
    today {at}
    future {on}
    other {}
  }`,
    description: 'Bag | Header | Time prefix',
  },
  time: {
    defaultMessage: `{state, select,
    future {{day}, {time}}
    other {{time}}
  }`,
    description: 'Bag | Header | Time prefix',
  },
  accessibilityLabel: {
    defaultMessage: 'Change order time',
    description: 'Bag | Header | Time a11y',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagTimeHeaderProps = {
  time?: WantedTime;
  isDisabled?: boolean;
  requestTimeChange: () => void;
};
